import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import React, { useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils/axiosError';
import AddMenuOverlayButton from '../components/AddMenuOverlayButton';
import OverlayDetailModal from '../components/OverlayDetailModal';
import { Overlay } from '../models/overlay';
import overlayService from '../overlayService';
import { overlayActions, selectStoreOverlayListData } from '../overlaySlice';

const OverlayFunction = () => {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  // selectors
  const overlays = useAppSelector(selectStoreOverlayListData);
  const isEditMode = useAppSelector(selectIsEditMode);

  useEffect(() => {
    dispatch(overlayActions.fetchOverlayList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(overlayActions.clearOverlayList());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // state
  const [isOpenAddNewOverlayModal, setIsOpenAddNewOverlayModal] = useState<Boolean>();
  const [isOpenDetailOverlayModal, setIsOpenDetailOverlayModal] = useState<Boolean>();
  const [selectedOverlayIds, setSelectedOverlayIds] = useState<number[]>([]);

  const refectchOverlayList = () => {
    dispatch(overlayActions.fetchOverlayList());
    setSelectedOverlayIds([]);
  };

  // modal action
  const handleOpenAddNewOverlayModal = () => {
    setIsOpenAddNewOverlayModal(true);
  };

  const handleCloseAddNewOverlayModal = () => {
    setIsOpenAddNewOverlayModal(false);
  };

  const handleOpenDetailOverlayModal = () => {
    setIsOpenDetailOverlayModal(true);
  };

  const handleCloseDetailOverlayModal = () => {
    setIsOpenDetailOverlayModal(false);
  };

  // overlay action
  const processDeleteOverlay = async (ids: number[]) => {
    try {
      const response = await overlayService.delete(ids);
      refectchOverlayList();
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
    } catch (e: any) {
      const deletableIds: number[] = e.response.data.data.delete;
      const no_delete: number[] = e.response.data.data.no_delete;
      const overlayNames = overlays
        .filter((overlay) => no_delete.includes(overlay.id))
        .map((overlay) => overlay.name)
        .join(', ');
      const can_delete_message =
        t('OverlayFunction.Warn.warn_confirm_no_delete', { name: `${overlayNames}` }) +
        '<br/>' +
        t('OverlayFunction.Warn.warn_confirm_can_delete');
      const no_delete_message = t('OverlayFunction.Warn.warn_confirm_no_delete', { name: `${overlayNames}` });

      if (no_delete) {
        if (deletableIds.length > 0) {
          showGlobalModal(() => (
            <ConfirmModal
              title={t('App.confirm')}
              message={can_delete_message}
              onConfirm={() => processDeleteOverlay(deletableIds)}
            />
          ));
        } else {
          showGlobalModal(() => <AlertModal title={t('App.info')} message={no_delete_message} />);
        }
      } else {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    }
  };

  const handleDeleteOverlay = () => {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('OverlayFunction.Warn.warn_confirm_delete')}
        onConfirm={() => processDeleteOverlay(selectedOverlayIds)}
      />
    ));
  };

  const processRestoreOverlay = async (id: number) => {
    try {
      const response = await overlayService.restore(id);
      refectchOverlayList();
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };

  const handleRestoreOverlay = (id: number) => {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('OverlayFunction.Warn.warn_confirm_restore')}
        onConfirm={() => processRestoreOverlay(id)}
      />
    ));
  };

  const handleSelectOverlay = (id: number) => {
    const existedOverlay = selectedOverlayIds.find((overlayId) => overlayId === id);
    if (existedOverlay) {
      setSelectedOverlayIds(selectedOverlayIds.filter((overlayId) => overlayId !== id));
    } else {
      setSelectedOverlayIds([...selectedOverlayIds, id]);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('OverlayFunction.Title.list_head_title')} />
          </div>
          <div className="text-end mb-3">
            <Button className="btn--dark-green" onClick={handleOpenAddNewOverlayModal}>
              {t('OverlayFunction.Button.add_new')}
            </Button>
          </div>
          <div className="text-end mb-3">
            <Button className="ms-3" onClick={handleOpenDetailOverlayModal} disabled={selectedOverlayIds.length !== 1}>
              {t('OverlayFunction.Button.detail')}
            </Button>
            <Button className="btn--red ms-3" onClick={handleDeleteOverlay} disabled={selectedOverlayIds.length <= 0}>
              {t('OverlayFunction.Button.delete')}
            </Button>
          </div>
          <div className="saletype-list-table sticky-table flex-grow-1 flex-shrink-1">
            <Table hover className="overlay-group-list">
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>{t('OverlayFunction.Table.selection')}</th>
                  <th style={{ width: '60%' }}>{t('OverlayFunction.Table.name')}</th>
                  <th style={{ width: '24%' }}>{t('OverlayFunction.Table.remarks')}</th>
                  <th style={{ width: '12%' }}>{t('OverlayFunction.Table.target_menu')}</th>
                </tr>
              </thead>
              <tbody>
                {overlays.map((overlay: Overlay, overlayIndex: number) => {
                  return (
                    <React.Fragment key={overlay.id}>
                      <tr
                        className={clsx([
                          { 'bg-light': overlay.delete_flg },
                          { 'last-row': overlayIndex === overlays?.length - 1 },
                        ])}
                      >
                        <td className="text-center">
                          {overlay.delete_flg && (
                            <Button className="btn--orange" onClick={() => handleRestoreOverlay(overlay.id)}>
                              {t('OverlayFunction.Button.restore')}
                            </Button>
                          )}
                          {!overlay.delete_flg && (
                            <Form.Check
                              name="selected_overlay"
                              value={overlay.id}
                              onChange={() => handleSelectOverlay(overlay.id)}
                              checked={selectedOverlayIds.includes(overlay.id)}
                            ></Form.Check>
                          )}
                        </td>
                        <td className="text-center">{overlay.name}</td>
                        <td className="text-center" dangerouslySetInnerHTML={{ __html: overlay.remarks || '' }}></td>
                        <td className="text-center">
                          {!overlay.delete_flg && (
                            <AddMenuOverlayButton overlayGroupId={overlay.id} overlayGroupName={overlay.name} />
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {isOpenAddNewOverlayModal && <OverlayDetailModal onClose={handleCloseAddNewOverlayModal} />}
      {isOpenDetailOverlayModal && (
        <OverlayDetailModal overlayId={selectedOverlayIds[0]} onClose={handleCloseDetailOverlayModal} />
      )}
    </div>
  );
};

export default OverlayFunction;
